import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { XMarkIcon } from '@heroicons/react/24/outline';

// Create a context for the notification system
const NotistackContext = createContext({
  enqueueSnackbar: () => {},
  closeSnackbar: () => {},
});

// Custom hook to use the notification system
export const useSnackbar = () => useContext(NotistackContext);

// Notification component
const Notification = ({ id, message, variant, onClose, autoHideDuration }) => {
  useEffect(() => {
    if (autoHideDuration) {
      const timer = setTimeout(() => {
        onClose(id);
      }, autoHideDuration);
      return () => clearTimeout(timer);
    }
  }, [id, onClose, autoHideDuration]);

  // Determine styles based on variant
  const getVariantStyles = () => {
    switch (variant) {
      case 'error':
        return 'bg-red-100 border-red-400 text-red-800';
      case 'warning':
        return 'bg-yellow-100 border-yellow-400 text-yellow-800';
      case 'info':
        return 'bg-blue-100 border-blue-400 text-blue-800';
      case 'success':
      default:
        return 'bg-green-100 border-green-400 text-green-800';
    }
  };

  return (
    <div 
      className={`rounded-md border p-4 shadow-md mb-2 flex items-center justify-between ${getVariantStyles()}`}
      role="alert"
    >
      <div className="text-sm font-medium">{message}</div>
      <button 
        onClick={() => onClose(id)} 
        className="ml-4 text-gray-400 hover:text-gray-600 focus:outline-none"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
};

Notification.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  message: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['default', 'error', 'success', 'warning', 'info']),
  onClose: PropTypes.func.isRequired,
  autoHideDuration: PropTypes.number,
};

// Main NotistackProvider component
export default function NotistackProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [counter, setCounter] = useState(0);

  // Add a new notification
  const enqueueSnackbar = (message, options = {}) => {
    const id = counter;
    setCounter(prev => prev + 1);
    
    const notification = {
      id,
      message,
      variant: options.variant || 'default',
      autoHideDuration: options.autoHideDuration || 5000,
      ...options
    };
    
    setNotifications(prev => [...prev, notification]);
    return id;
  };

  // Remove a notification
  const closeSnackbar = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  // Context value
  const contextValue = {
    enqueueSnackbar,
    closeSnackbar
  };

  return (
    <NotistackContext.Provider value={contextValue}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col-reverse items-end space-y-reverse space-y-2 max-w-md">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            id={notification.id}
            message={notification.message}
            variant={notification.variant}
            onClose={closeSnackbar}
            autoHideDuration={notification.autoHideDuration}
          />
        ))}
      </div>
    </NotistackContext.Provider>
  );
}

NotistackProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
